/**
 * Live PUBLIC API (no auth) — resident intake + tracking. Relative `/api/public/...` (same-origin
 * behind CloudFront). Shapes match the deployed Go handlers (services/api/internal/handler/public.go
 * + shared/projection.go). NOTE: submit carries NO captcha token (the field was removed from the
 * model). All error handling flows through apiFetch → ApiError.
 */
import type { SubmitReportInput } from "@td/shared";
import { apiFetch, putToPresignedUrl, ApiError } from "./client.js";
import type { EmbedConfig } from "../theme/embedConfig.js";
import { deriveEmbedConfig, type RawEmbedConfig } from "./embedConfigMap.js";
import type { IntakeCategory } from "./categories.js";
import type { QuestionTopic } from "./questionTopics.js";
import type { ChannelView } from "./types.js";
import type { KORARecordType } from "./koraRecordTypes.js";
import type { PermitType } from "./permitTypes.js";
import type { KORAEmbedConfig } from "./embedConfigMap.js";

// ---- Embed config (GET /api/public/embed-config) ---------------------------

export async function fetchEmbedConfigLive(): Promise<{
  config: EmbedConfig;
  categories: IntakeCategory[];
  questionTopics: QuestionTopic[];
  channels: ChannelView[];
  koraRecordTypes: KORARecordType[];
  permitTypes: PermitType[];
  kora: KORAEmbedConfig;
  publicShowAgendaItemDetails: boolean;
  publicShowAttachments: boolean;
}> {
  const raw = await apiFetch<RawEmbedConfig>("/public/embed-config");
  return deriveEmbedConfig(raw);
}

// ---- Presigned upload (POST /api/public/uploads → PUT to S3) ----------------

interface PresignResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

/** Presign + PUT a resident photo; returns the stored S3 key (goes in submit.attachments). */
export async function uploadAttachmentLive(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const presign = await apiFetch<PresignResponse>("/public/uploads", {
    method: "POST",
    body: { contentType: file.type, fileName: file.name },
  });
  await putToPresignedUrl(presign.uploadUrl, file, file.type, onProgress);
  return presign.s3Key;
}

// ---- Reverse geocode (GET /api/public/reverse-geocode) ----------------------
//
// Public, no auth (same WAF origin-lockdown + rate-limit + CORS as embed-config — NOT the CAPTCHA
// action). Backend resolves lat/lng via Amazon Location (`td-places` Place Index) and returns the
// top result's label, or null when there's no match. No PII: the resident is anonymous and we do
// not log the raw coordinates client-side.
export async function reverseGeocodeLive(lat: number, lng: number): Promise<string | null> {
  const res = await apiFetch<{ address: string | null }>("/public/reverse-geocode", {
    query: { lat: String(lat), lng: String(lng) },
  });
  return res.address ?? null;
}

// ---- Submit report (POST /api/public/requests) ------------------------------

export interface SubmitReportResult {
  trackingCode: string;
  status: string;
  trackingUrl: string;
  /** kept for parity with the mock; not returned by the API. */
  requestId?: string;
}

export async function submitReportLive(input: SubmitReportInput): Promise<SubmitReportResult> {
  const res = await apiFetch<{ trackingCode: string; status: string; trackingUrl?: string }>(
    "/public/requests",
    { method: "POST", body: input },
  );
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    trackingCode: res.trackingCode,
    status: res.status,
    trackingUrl: res.trackingUrl ?? `${origin}/track/${res.trackingCode}`,
  };
}

// ---- Track (GET /api/public/requests/track/{code}) --------------------------

import type { RequestStatus } from "@td/shared";
import type { AttachmentView } from "./types.js";
import type { PublicTracking } from "./trackingTypes.js";

/** The raw public projection returned by the API (shared/projection.go PublicRequestView). Widened
 *  to "kora"/"permit" (records-request.md / roadmap module 6) — a KORA/permit tracking page must
 *  render as KORA/permit, never silently fall back to "concern". */
interface PublicRequestView {
  trackingCode: string;
  kind?: "concern" | "question" | "kora" | "permit";
  status: RequestStatus;
  category: string;
  description: string;
  location: { address: string; lat?: number; lng?: number };
  createdAt: string;
  updatedAt: string;
  updates: Array<{ type: string; body: string; createdAt: string }>;
  attachments: Array<{ kind: string; s3Key: string; createdAt: string }>;
  /** Server-side existence flag for a satisfaction survey (shared/projection.go PublicRequestView.SurveySubmitted).
   *  Source of truth on every fresh page load — survives reloads, unlike client-only session state. */
  surveySubmitted?: boolean;
}

export async function getPublicTrackingLive(code: string): Promise<PublicTracking | null> {
  let view: PublicRequestView;
  try {
    view = await apiFetch<PublicRequestView>(`/public/requests/track/${encodeURIComponent(code)}`);
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
  return mapPublicView(view);
}

// ---- Resident reply / comment (POST .../track/{code}/comments) -------------
//
// Public, no auth, tracking-code-gated (report-a-concern.md "Resident can reply / add a comment").
// Tenant + request id are resolved SERVER-SIDE from the tracking code (GSI2) — NEVER sent in the
// body (NFR-11). Same CAPTCHA contract as submitReportLive/subscribeLive: the caller MUST resolve
// `ensureCaptchaToken(container)` (wafCaptcha.ts) before calling this — the solved WAF token then
// rides the POST as the `x-aws-waf-token` header (attached by apiFetch; cookie-independent so it
// also works inside the cross-site embed iframe) AND as the same-origin `aws-waf-token` cookie, and
// the edge `captcha-public-submit` WAF rule (now covering the `/comments` and `/satisfaction`
// suffixes too) accepts either. `captchaToken` is accepted here only for parity with the request body
// shape (shared/schemas.go AddResidentCommentInput.CaptchaToken) — the server does not yet enforce
// it in-handler, matching submitReportLive's pattern.
export async function addResidentCommentLive(
  code: string,
  body: string,
  captchaToken?: string,
): Promise<{ id: string; body: string; createdAt: string }> {
  const res = await apiFetch<{ createdAt?: string }>(
    `/public/requests/track/${encodeURIComponent(code)}/comments`,
    { method: "POST", body: { body, captchaToken: captchaToken || undefined } },
  );
  const createdAt = res.createdAt ?? new Date().toISOString();
  return { id: `resident-comment-${createdAt}`, body, createdAt };
}

// ---- Satisfaction survey (POST .../track/{code}/satisfaction) --------------
//
// Public, no auth, tracking-code-gated; the backend resolves the request from the code and only
// accepts the write when its status is Resolved (report-a-concern.md "Satisfaction survey after
// resolution"). One survey per request (last-write-wins server-side) — the TrackingPage hides the
// card after a successful submit so this is only ever called once per session. Same CAPTCHA
// contract as addResidentCommentLive: call ensureCaptchaToken(container) first.
export async function submitSatisfactionSurveyLive(
  code: string,
  rating: number,
  comment?: string,
): Promise<void> {
  await apiFetch(`/public/requests/track/${encodeURIComponent(code)}/satisfaction`, {
    method: "POST",
    body: { rating, comment: comment || undefined },
  });
}

// ---- Notify: subscribe / confirm / unsubscribe (ADR-0012, notify/spec.md) --------------------
//
// Endpoint list per ADR-0012 §Scope + notify/spec.md: POST /public/notify/subscribe,
// GET /public/notify/confirm?token=, GET /public/notify/unsubscribe?token=. Backend handlers are
// deployed (internal/handler/public.go) and match these shapes.

import type {
  SubscribeResult,
  NotifyLandingResult,
  PreferencesResult,
  PreferencesUpdateResult,
  DeliveryMethod,
  MailingAddress,
} from "./types.js";

/**
 * POST /public/notify/subscribe. Body: { email?, name?, channels?, deliveryMethod?, mailingAddress? }.
 * Response is ALWAYS success-shaped (spec §1.6 anti-enumeration — the server never reveals whether
 * the email was already on the list); the only variance is `status` (pending vs. already-confirmed
 * for email/both, or confirmed immediately for mail-only single-opt-in — postal-updates.md), which
 * the UI uses only to choose the matching success copy — never a failure state. `channels` is the
 * resident's checked channel ids (stay-informed-channels.md §3.1); an empty/omitted array means
 * "everything" — never expanded client-side. `email` is REQUIRED iff deliveryMethod ∈
 * {email, both} (omitted deliveryMethod defaults to "email" server-side); `mailingAddress` is
 * REQUIRED iff deliveryMethod ∈ {mail, both} (postal-updates.md wire contract — pinned exact shape).
 */
export async function subscribeLive(input: {
  email?: string;
  name?: string;
  channels?: string[];
  deliveryMethod?: DeliveryMethod;
  mailingAddress?: MailingAddress;
}): Promise<SubscribeResult> {
  return apiFetch<SubscribeResult>("/public/notify/subscribe", {
    method: "POST",
    body: {
      email: input.email || undefined,
      name: input.name || undefined,
      channels: input.channels?.length ? input.channels : undefined,
      deliveryMethod:
        input.deliveryMethod && input.deliveryMethod !== "email" ? input.deliveryMethod : undefined,
      mailingAddress: input.mailingAddress,
    },
  });
}

/** POST /public/notify/subscribe/resend — re-send the confirmation email (rate-limited server-side,
 *  spec §1.6). Body: { email }. */
export async function resendConfirmationLive(email: string): Promise<void> {
  await apiFetch("/public/notify/subscribe/resend", { method: "POST", body: { email } });
}

/** POST /public/notify/manage-link — "email me my manage link" (subscriber-self-service.md Part A).
 *  ALWAYS resolves `{status:"ok"}` regardless of whether the address is on file (anti-enumeration,
 *  same posture as subscribe/resend) — captcha + rate-limited server-side. Body: { email }. */
export async function manageLinkLive(email: string): Promise<{ status: "ok" }> {
  return apiFetch<{ status: "ok" }>("/public/notify/manage-link", {
    method: "POST",
    body: { email },
  });
}

/** GET /public/notify/confirm?token=<token> — executes the double-opt-in confirm (spec §2.2). */
export async function confirmSubscriptionLive(token: string): Promise<NotifyLandingResult> {
  return apiFetch<NotifyLandingResult>("/public/notify/confirm", { query: { token } });
}

/** GET /public/notify/unsubscribe?token=<token> — one-click unsubscribe, executes on load, no
 *  confirmation step (spec §3.1 — CAN-SPAN "no additional information required"). */
export async function unsubscribeLive(token: string): Promise<NotifyLandingResult> {
  return apiFetch<NotifyLandingResult>("/public/notify/unsubscribe", { query: { token } });
}

// ---- Notify: preferences (stay-informed-channels.md §3.2, Phase 4) --------------------------
//
// GET/PUT /public/preferences?token=<token> — same anti-enumeration + no-login model as
// confirm/unsubscribe (the token is the SAME permanent UnsubToken, backend contract per the
// arch doc §3.2 "pick per the arch doc" / Phase 2 backend report). `?token=` query param, matching
// the Confirm/Unsubscribe convention (the backend also accepts a `{token}` path segment/param, but
// the query form is what's guaranteed wired without an infra route change).

/** GET /public/preferences?token=<token> — the subscriber's current channel selection + the
 *  tenant's active channel list to render checkboxes against. Also carries `deliveryMethod`
 *  (normalized, never "") and `mailingAddress` (object or null) — postal-updates.md extension. */
export async function getPreferencesLive(token: string): Promise<PreferencesResult> {
  return apiFetch<PreferencesResult>("/public/preferences", { query: { token } });
}

/** PUT /public/preferences?token=<token>. Body: { channels, deliveryMethod?, mailingAddress? }. An
 *  empty `channels` array routes to a REAL unsubscribe server-side (§3.2 "uncheck-all →
 *  unsubscribe") — never silently stored as Channels=nil (which would mean "everything," the
 *  opposite of unchecking everything). `deliveryMethod`/`mailingAddress` are optional — omitted
 *  means no change (postal-updates.md wire contract). */
export async function updatePreferencesLive(
  token: string,
  channels: string[],
  deliveryMethod?: DeliveryMethod,
  mailingAddress?: MailingAddress,
): Promise<PreferencesUpdateResult> {
  return apiFetch<PreferencesUpdateResult>("/public/preferences", {
    method: "PUT",
    query: { token },
    body: { channels, deliveryMethod, mailingAddress },
  });
}

// ---- Ask a Question + FAQ (ask-a-question-faq.md) ---------------------------
//
// POST /public/questions (♻️ submit-report shape), GET /public/faq(+/search,/{id}),
// POST /public/faq/{id}/vote. Same public-write WAF captcha/rate-limit posture as the concern
// intake + resident comment (ADR-0004 / NFR-11-alike): tenant is server-resolved, never sent here.

import type { FAQView } from "./types.js";

interface WireFAQ {
  id: string;
  topicId: string;
  topicLabel?: string;
  question: string;
  answerHtml: string;
  published: boolean;
  featured: boolean;
  order: number;
  viewCount: number;
  helpfulYes: number;
  helpfulNo: number;
  sourceQuestionIds?: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

function mapFAQ(f: WireFAQ): FAQView {
  return {
    id: f.id,
    topicId: f.topicId,
    topicLabel: f.topicLabel,
    question: f.question,
    answerHtml: f.answerHtml,
    published: f.published,
    featured: f.featured,
    order: f.order,
    viewCount: f.viewCount,
    helpfulYes: f.helpfulYes,
    helpfulNo: f.helpfulNo,
    sourceQuestionIds: f.sourceQuestionIds,
    createdAt: f.createdAt,
    updatedAt: f.updatedAt,
    publishedAt: f.publishedAt,
  };
}

export interface SubmitQuestionInput {
  topic: string;
  question: string;
  reporterContact?: { name?: string; email?: string; phone?: string };
}

/** POST /public/questions — ♻️ same shape/response as submitReportLive, Kind=question server-set. */
export async function submitQuestionLive(input: SubmitQuestionInput): Promise<SubmitReportResult> {
  const res = await apiFetch<{ trackingCode: string; status: string; trackingUrl?: string }>(
    "/public/questions",
    { method: "POST", body: input },
  );
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    trackingCode: res.trackingCode,
    status: res.status,
    trackingUrl: res.trackingUrl ?? `${origin}/track/${res.trackingCode}`,
  };
}

/** GET /public/faq — browse published FAQs (featured-first, by topic). */
export async function browseFAQLive(topic?: string): Promise<FAQView[]> {
  const res = await apiFetch<{ faqs: WireFAQ[]; count: number }>("/public/faq", {
    query: { topic },
  });
  return res.faqs.map(mapFAQ);
}

/** GET /public/faq/search?q=&topic= — keyword search (also the related-FAQ typeahead backing
 *  query, §5.1 "deflect before asking"). */
export async function searchFAQLive(q: string, topic?: string): Promise<FAQView[]> {
  const res = await apiFetch<{ faqs: WireFAQ[]; count: number }>("/public/faq/search", {
    query: { q, topic },
  });
  return res.faqs.map(mapFAQ);
}

/** GET /public/faq/{id} — single published entry; fires a fire-and-forget view-count bump
 *  server-side (§5.3). Returns null on 404 (unpublished/unknown id). */
export async function getFAQLive(id: string): Promise<FAQView | null> {
  try {
    const f = await apiFetch<WireFAQ>(`/public/faq/${encodeURIComponent(id)}`);
    return mapFAQ(f);
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

/** POST /public/faq/{id}/vote — "was this helpful?" (§5.3), WAF rate-limited at the gateway. */
export async function voteFAQLive(id: string, helpful: boolean): Promise<void> {
  await apiFetch(`/public/faq/${encodeURIComponent(id)}/vote`, {
    method: "POST",
    body: { helpful },
  });
}

// ---- Open Records (KORA) requests (records-request.md) ----------------------
//
// POST /public/records-requests (♻️ submit-report/submit-question shape). Tenant is ALWAYS
// server-resolved from TD_TENANT_ID, never sent here. Same public-write WAF captcha/rate-limit
// posture as the concern/question intake (ADR-0004 / NFR-11-alike). KORA allows anonymous
// requests — `requesterName`/`requesterContact` are optional.

import type { SubmitKORAInput } from "./types.js";

/** POST /public/records-requests — ♻️ same trackingCode/status/trackingUrl response shape as
 *  submitReportLive/submitQuestionLive, Kind=kora server-set. */
export async function submitKORALive(input: SubmitKORAInput): Promise<SubmitReportResult> {
  const res = await apiFetch<{ trackingCode: string; status: string; trackingUrl?: string }>(
    "/public/records-requests",
    { method: "POST", body: input },
  );
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    trackingCode: res.trackingCode,
    status: res.status,
    trackingUrl: res.trackingUrl ?? `${origin}/track/${res.trackingCode}`,
  };
}

// ---- Permits & Licensing (roadmap module 6) ----------------------------------
//
// POST /public/permits (♻️ submit-kora shape). Tenant is ALWAYS server-resolved from TD_TENANT_ID,
// never sent here. Same public-write WAF captcha/rate-limit posture as the concern/question/KORA
// intake (ADR-0004 / NFR-11-alike). Unlike KORA, an applicant is normally known
// (applicantName/applicantContact are optional-but-expected, not anonymous-first).

import type { SubmitPermitInput } from "./types.js";

/** POST /public/permits — ♻️ same trackingCode/status/trackingUrl response shape as
 *  submitReportLive/submitKORALive, Kind=permit server-set. */
export async function submitPermitLive(input: SubmitPermitInput): Promise<SubmitReportResult> {
  const res = await apiFetch<{ trackingCode: string; status: string; trackingUrl?: string }>(
    "/public/permits",
    { method: "POST", body: input },
  );
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    trackingCode: res.trackingCode,
    status: res.status,
    trackingUrl: res.trackingUrl ?? `${origin}/track/${res.trackingCode}`,
  };
}

/** Map the API projection → the view model the TrackingPage renders. */
function mapPublicView(v: PublicRequestView): PublicTracking {
  // The projection carries S3 keys, not signed URLs, for public attachments. Public GETs are not
  // presigned by the track endpoint (v1), so we render tiles without a live thumbnail URL.
  const publicAttachments: AttachmentView[] = v.attachments.map((a, i) => ({
    id: `att-${i}`,
    url: "",
    filename: a.s3Key.split("/").pop() ?? "attachment",
    contentType: "",
    kind: (a.kind as AttachmentView["kind"]) ?? "other",
    visibility: "public",
    createdAt: a.createdAt,
    isImage: a.kind === "resident-photo" || a.kind === "site-photo",
  }));

  return {
    trackingCode: v.trackingCode,
    kind:
      v.kind === "question"
        ? "question"
        : v.kind === "kora"
          ? "kora"
          : v.kind === "permit"
            ? "permit"
            : "concern",
    status: v.status,
    category: v.category,
    description: v.description,
    address: v.location.address,
    // The public projection deliberately omits reporter contact; "hasContact" is unknown to the
    // resident-facing layer, so we present the anonymous-safe copy (check back here for updates).
    hasContact: false,
    createdAt: v.createdAt,
    updatedAt: v.updatedAt,
    updates: v.updates.map((u, i) => ({ id: `u-${i}`, body: u.body, createdAt: u.createdAt })),
    publicAttachments,
    ownSubmission: {
      description: v.description,
      photos: publicAttachments.filter((a) => a.kind === "resident-photo"),
    },
    surveySubmitted: v.surveySubmitted,
  };
}

// ---- Council Agenda + Packet — PUBLIC /meetings (agenda-packet.md §6/§10A Phase 4) -------------
//
// GET /api/public/meetings, GET /api/public/meetings/{id} — no auth (mirrors PublicFAQ's trust
// posture), byte-exact mirror of handler/meetings_public.go's allowlist projections.

import type { PublicMeetingView, PublicMeetingDetail } from "./types.js";

/** GET /api/public/meetings — published (+ archived) meetings, soonest-upcoming first then
 *  most-recent-past. */
export async function listPublicMeetingsLive(): Promise<PublicMeetingView[]> {
  const res = await apiFetch<{ meetings: PublicMeetingView[]; count: number }>("/public/meetings");
  return res.meetings ?? [];
}

/** GET /api/public/meetings/{id} — the published detail (notice-block fields, agenda items in
 *  order with Outcome/Vote + described attachments, minutes if posted+published). A draft meeting
 *  id 404s (anti-enumeration — never distinguishes "draft" from "doesn't exist"). */
export async function getPublicMeetingLive(id: string): Promise<PublicMeetingDetail | null> {
  try {
    return await apiFetch<PublicMeetingDetail>(`/public/meetings/${encodeURIComponent(id)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// ---- Council Agenda + Packet — Contributor "My reports" (agenda-packet.md §3.2/§10A Phase 3b) --
//
// GET/PUT/POST /api/contrib/* — a RoleContributor (or admin) staff token via apiFetch's `auth: true`
// (the SAME Cognito staff session as the admin console; contributorAuth in-handler gates the role).
// Byte-exact mirror of handler/contributor.go's route table + allowlist projections.

import type {
  MyReportSlot,
  ContributorContributionView,
  ContributorMeetingView,
  ContributorAgendaItemView,
  SaveContributionInput as ContribSaveInput,
  AttachMeetingFileInput as ContribAttachInput,
  MeetingAttachmentView as ContribAttachmentView,
} from "./types.js";

/** GET /api/contrib/my-reports — ONLY the caller's assigned slots for upcoming meetings (an admin
 *  caller sees every upcoming contribution — the clerk's oversight view, reached through the same
 *  route). */
export async function listMyReportsLive(): Promise<MyReportSlot[]> {
  const res = await apiFetch<{ reports: MyReportSlot[] }>("/contrib/my-reports", { auth: true });
  return res.reports ?? [];
}

/** GET /api/contrib/meetings/{id}/items/{itemId}/contribution — read the caller's own slot. */
export async function getMyContributionLive(
  meetingId: string,
  itemId: string,
): Promise<{
  contribution: ContributorContributionView;
  meeting: ContributorMeetingView;
  item: ContributorAgendaItemView;
} | null> {
  try {
    return await apiFetch(
      `/contrib/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/contribution`,
      { auth: true },
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** PUT /api/contrib/meetings/{id}/items/{itemId}/contribution — write the caller's OWN slot's report
 *  HTML (a checkpoint save — Requested moves to Drafted implicitly on first save; never accepts a
 *  state transition itself, see submitMyContributionLive). */
export async function saveMyContributionLive(
  meetingId: string,
  itemId: string,
  input: ContribSaveInput,
): Promise<ContributorContributionView> {
  const res = await apiFetch<{ contribution: ContributorContributionView }>(
    `/contrib/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/contribution`,
    { method: "PUT", auth: true, body: input },
  );
  return res.contribution;
}

/** POST /api/contrib/meetings/{id}/items/{itemId}/contribution/submit — flip the caller's own slot
 *  Requested/Drafted -> Submitted. 409s on an illegal transition (e.g. already Included). */
export async function submitMyContributionLive(
  meetingId: string,
  itemId: string,
): Promise<ContributorContributionView> {
  const res = await apiFetch<{ contribution: ContributorContributionView }>(
    `/contrib/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/contribution/submit`,
    { method: "POST", auth: true },
  );
  return res.contribution;
}

/** POST /api/contrib/meetings/{id}/items/{itemId}/contribution/attachments — register a file already
 *  uploaded via the shared presigned-doc-upload path as an attachment on the caller's OWN
 *  contribution slot. */
export async function attachMyContributionFileLive(
  meetingId: string,
  itemId: string,
  input: ContribAttachInput,
): Promise<ContribAttachmentView> {
  const res = await apiFetch<{ attachment: ContribAttachmentView }>(
    `/contrib/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/contribution/attachments`,
    { method: "POST", auth: true, body: input },
  );
  return res.attachment;
}
